/**
 * URL helpers for pagination management
 * Centralizes page URL construction logic to avoid duplication across Buy/Developers/Rent pages
 */

import { createLogger } from "@/utils/logger";

const logger = createLogger("URLPagination");

/**
 * Extract page number from pathname
 * @param pathname - Current pathname (e.g., "/buy/page/3" or "/buy")
 * @returns Page number (default 1 if not found)
 */
export const extractPageFromPath = (pathname: string): number => {
  if (!pathname) return 1;

  // Match /page/N with or without trailing slash
  const match = pathname.match(/\/page\/(\d+)\/?$/);
  return match ? parseInt(match[1], 10) : 1;
};

/**
 * Build page URL for path-based pagination
 * @param pathname - Current pathname
 * @param searchParams - Current URL search parameters
 * @param page - Target page number
 * @returns Complete URL with page path and query parameters
 */
export const buildPageUrl = (
  pathname: string,
  searchParams: URLSearchParams,
  page: number,
): string => {
  // Safety check for undefined pathname
  if (!pathname) {
    logger.warn('buildPageUrl', 'Called with undefined pathname');
    return '/';
  }
  
  // Remove existing /page/N from pathname to get base path
  const basePath = pathname.replace(/\/page\/\d+\/?$/, "");

  // Normalize base path - ensure it ends with a single slash
  const normalizedBasePath = basePath.endsWith("/") ? basePath : `${basePath}/`;

  // For page 1, use base path; for others, use /page/N/
  const targetPath =
    page === 1 ? normalizedBasePath : `${normalizedBasePath}page/${page}/`;

  // Build query string without page parameter (path-based pagination)
  const filteredParams = new URLSearchParams(searchParams.toString());
  filteredParams.delete("page");

  const queryString = filteredParams.toString();
  return queryString ? `${targetPath}?${queryString}` : targetPath;
};

/**
 * Build URL with updated limit parameter
 * @param basePath - Base path without page segments
 * @param searchParams - Current URL search parameters
 * @param nextLimit - New page size limit
 * @returns Object with pathname and query parameters for router navigation
 */
export const buildLimitUrl = (
  basePath: string,
  searchParams: URLSearchParams,
  nextLimit: number,
): { pathname: string; query: Record<string, string> } => {
  // Safety check for undefined basePath
  if (!basePath) {
    return {
      pathname: '/',
      query: { limit: String(nextLimit) },
    };
  }
  
  const params = new URLSearchParams(searchParams.toString());

  // Set new limit and remove page (reset to page 1 when changing page size)
  params.set("limit", String(nextLimit));
  params.delete("page");

  // Clean base path (remove any /page/N segments)
  const cleanPath = basePath.replace(/\/page\/\d+\/?$/, "");

  return {
    pathname: cleanPath,
    query: Object.fromEntries(params.entries()),
  };
};

/**
 * Check if current page is different from URL page
 * Utility for determining when to trigger navigation
 * @param pathname - Current pathname
 * @param currentPage - Current page state
 * @returns true if page numbers differ
 */
export const shouldUpdatePageUrl = (
  pathname: string,
  currentPage: number,
): boolean => {
  const urlPage = extractPageFromPath(pathname);
  return urlPage !== currentPage;
};

/**
 * Build page URL for query-based pagination
 * @param pathname - Current pathname (unchanged)
 * @param searchParams - Current URL search parameters
 * @param page - Target page number
 * @returns Complete URL with page in query parameters
 */
export const buildQueryPageUrl = (
  pathname: string,
  searchParams: URLSearchParams,
  page: number,
): string => {
  // Safety check for undefined pathname
  if (!pathname) {
    logger.warn('buildQueryPageUrl', 'Called with undefined pathname');
    return '/';
  }
  
  const params = new URLSearchParams(searchParams.toString());

  // Normalize pathname - ensure it ends with slash if it doesn't have query params
  const normalizedPathname = pathname.endsWith("/") ? pathname : `${pathname}/`;

  if (page === 1) {
    // Remove page parameter for page 1
    params.delete("page");
  } else {
    // Set page parameter for other pages
    params.set("page", String(page));
  }

  const queryString = params.toString();
  return queryString
    ? `${normalizedPathname}?${queryString}`
    : normalizedPathname;
};

/**
 * Get pagination URL info for current state
 * @param pathname - Current pathname
 * @param searchParams - Current search parameters
 * @returns Object with current page and base path info
 */
export const getPaginationInfo = (
  pathname: string,
  searchParams: URLSearchParams,
) => {
  // Safety check for undefined pathname
  if (!pathname) {
    return {
      currentPage: 1,
      basePath: '/',
      hasSearchFilters: false,
    };
  }
  
  const currentPage = extractPageFromPath(pathname);
  const basePath = pathname.replace(/\/page\/\d+\/?$/, "");
  const hasSearchFilters = searchParams.toString().length > 0;

  return {
    currentPage,
    basePath,
    hasSearchFilters,
  };
};
